import React, { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, Quote, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const I18N = {
  uk: {
    kicker: "ВІДЕО-ВІДГУКИ",
    title: "Реальні історії наших студентів",
    subtitle: "Послухай тих, хто вже здійснив мрію навчатися за кордоном разом зі STUDYGRI",
    playLabel: "Дивитися відгук",
    pauseLabel: "Пауза"
  },
  ru: {
    kicker: "ВИДЕО-ОТЗЫВЫ",
    title: "Реальные истории наших студентов",
    subtitle: "Послушай тех, кто уже осуществил мечту учиться за рубежом со STUDYGRI",
    playLabel: "Смотреть отзыв",
    pauseLabel: "Пауза"
  },
  en: {
    kicker: "VIDEO REVIEWS",
    title: "Real stories from our students",
    subtitle: "Hear from those who have already made their dream of studying abroad come true with STUDYGRI",
    playLabel: "Watch review",
    pauseLabel: "Pause"
  },
  pl: {
    kicker: "VIDEO-OPINIE",
    title: "Prawdziwe historie naszych studentów",
    subtitle: "Posłuchaj tych, którzy spełnili marzenie o studiowaniu za granicą ze STUDYGRI",
    playLabel: "Zobacz opinię",
    pauseLabel: "Pauza"
  },
  it: {
    kicker: "VIDEO RECENSIONI",
    title: "Storie vere dei nostri studenti",
    subtitle: "Ascolta chi ha già realizzato il sogno di studiare all'estero con STUDYGRI",
    playLabel: "Guarda la recensione",
    pauseLabel: "Pausa"
  },
  es: {
    kicker: "VIDEO RESEÑAS",
    title: "Historias reales de nuestros estudiantes",
    subtitle: "Escucha a quienes ya cumplieron el sueño de estudiar en el extranjero con STUDYGRI",
    playLabel: "Ver reseña",
    pauseLabel: "Pausa"
  },
  ar: {
    kicker: "تقييمات فيديو",
    title: "قصص حقيقية من طلابنا",
    subtitle: "استمع لمن حققوا حلم الدراسة في الخارج مع STUDYGRI",
    playLabel: "شاهد التقييم",
    pauseLabel: "إيقاف"
  },
  zh: {
    kicker: "视频评价",
    title: "我们学生的真实故事",
    subtitle: "聆听那些已经通过 STUDYGRI 实现海外求学梦想的学生",
    playLabel: "观看评价",
    pauseLabel: "暂停"
  }
};

// Video reviews — more can be added by user later
const REVIEWS = [
  { id: "review-1", src: "/videos/review1.mp4", poster: "/videos/review1-poster.jpg",
    name: { uk: "Студент STUDYGRI", ru: "Студент STUDYGRI", en: "STUDYGRI student", pl: "Student STUDYGRI", it: "Studente STUDYGRI", es: "Estudiante STUDYGRI", ar: "طالب STUDYGRI", zh: "STUDYGRI 学生" } },
  { id: "review-2", src: "/videos/review2.mp4", poster: "/videos/review2-poster.jpg",
    name: { uk: "Студент STUDYGRI", ru: "Студент STUDYGRI", en: "STUDYGRI student", pl: "Student STUDYGRI", it: "Studente STUDYGRI", es: "Estudiante STUDYGRI", ar: "طالب STUDYGRI", zh: "STUDYGRI 学生" } },
  { id: "review-3", src: "/videos/review3.mp4", poster: "/videos/review3-poster.jpg",
    name: { uk: "Студент STUDYGRI", ru: "Студент STUDYGRI", en: "STUDYGRI student", pl: "Student STUDYGRI", it: "Studente STUDYGRI", es: "Estudiante STUDYGRI", ar: "طالب STUDYGRI", zh: "STUDYGRI 学生" } },
  { id: "review-4", src: "/videos/review4.mp4", poster: "/videos/review4-poster.jpg",
    name: { uk: "Студент STUDYGRI", ru: "Студент STUDYGRI", en: "STUDYGRI student", pl: "Student STUDYGRI", it: "Studente STUDYGRI", es: "Estudiante STUDYGRI", ar: "طالب STUDYGRI", zh: "STUDYGRI 学生" } },
  { id: "review-5", src: "/videos/review5.mp4", poster: "/videos/review5-poster.jpg",
    name: { uk: "Студент STUDYGRI", ru: "Студент STUDYGRI", en: "STUDYGRI student", pl: "Student STUDYGRI", it: "Studente STUDYGRI", es: "Estudiante STUDYGRI", ar: "طالب STUDYGRI", zh: "STUDYGRI 学生" } },
  { id: "review-6", src: "/videos/review6.mp4", poster: "/videos/review6-poster.jpg",
    name: { uk: "Студент STUDYGRI", ru: "Студент STUDYGRI", en: "STUDYGRI student", pl: "Student STUDYGRI", it: "Studente STUDYGRI", es: "Estudiante STUDYGRI", ar: "طالب STUDYGRI", zh: "STUDYGRI 学生" } }
];

const VideoCard = ({ review, t, lang, idx }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);

  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
      setStarted(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div
      className="group relative rounded-[28px] overflow-hidden transition-all duration-500 hover:-translate-y-1"
      style={{
        background: 'linear-gradient(180deg, #1B2A4A 0%, #0F1A30 100%)',
        boxShadow: '0 20px 50px -20px rgba(27,42,74,0.55), 0 0 0 1px rgba(201,169,110,0.25)',
        aspectRatio: '9 / 16'
      }}
      data-testid={`video-review-card-${idx}`}
    >
      {/* Gold corner accents */}
      <div className="pointer-events-none absolute top-0 left-0 w-16 h-16 z-[3]" style={{ background: 'radial-gradient(circle at top left, rgba(201,169,110,0.45) 0%, transparent 60%)' }} />
      <div className="pointer-events-none absolute bottom-0 right-0 w-24 h-24 z-[3]" style={{ background: 'radial-gradient(circle at bottom right, rgba(201,169,110,0.35) 0%, transparent 65%)' }} />

      {/* Video element */}
      <video
        ref={videoRef}
        src={review.src}
        poster={review.poster}
        preload="metadata"
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        controls={started}
        data-testid={`video-review-player-${idx}`}
      />

      {/* Dark gradient overlay (only before play) — keeps poster visible but readable */}
      {!started && (
        <div className="absolute inset-0 z-[2] pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(15,26,48,0) 0%, rgba(15,26,48,0) 40%, rgba(15,26,48,0.75) 70%, rgba(15,26,48,0.97) 100%)' }} />
      )}

      {/* Quote icon top-left */}
      {!started && (
        <div className="absolute top-5 left-5 z-[3] w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(201,169,110,0.18)', border: '1px solid rgba(201,169,110,0.45)', backdropFilter: 'blur(8px)' }}>
          <Quote className="w-4 h-4" style={{ color: '#C9A96E' }} />
        </div>
      )}

      {/* Bottom info + play button */}
      {!started && (
        <div className="absolute inset-x-0 bottom-0 z-[3] p-6 md:p-7">
          <div className="text-[10px] font-bold uppercase tracking-[0.28em] mb-2.5" style={{ color: '#C9A96E' }}>
            <Sparkles className="w-3 h-3 inline-block mr-1.5 -mt-0.5" />
            STUDYGRI · Real story
          </div>
          <div className="font-display text-xl md:text-2xl leading-tight mb-5" style={{ color: '#FFFFFF', fontWeight: 800, letterSpacing: '-0.01em' }}>
            {review.name[lang] || review.name.uk}
          </div>

          <button
            onClick={handlePlay}
            className="inline-flex items-center gap-3 pl-2 pr-6 h-12 rounded-full font-semibold text-sm transition-all duration-300 hover:gap-4"
            style={{ background: '#C9A96E', color: '#1B2A4A', boxShadow: '0 10px 25px -8px rgba(201,169,110,0.6)' }}
            data-testid={`video-review-play-${idx}`}
            aria-label={t.playLabel}
          >
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#1B2A4A', color: '#C9A96E' }}>
              <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
            </span>
            {t.playLabel}
          </button>
        </div>
      )}

      {/* Pause overlay button when playing (subtle) */}
      {started && playing && (
        <button
          onClick={handlePlay}
          className="absolute top-4 right-4 z-[4] w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110"
          style={{ background: 'rgba(27,42,74,0.78)', border: '1px solid rgba(201,169,110,0.5)', backdropFilter: 'blur(10px)' }}
          aria-label={t.pauseLabel}
          data-testid={`video-review-pause-${idx}`}
        >
          <Pause className="w-4 h-4" style={{ color: '#C9A96E' }} fill="currentColor" />
        </button>
      )}
    </div>
  );
};

const VideoReviews = () => {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.uk;
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(4);

  // Responsive visible count
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setVisible(1);
      else if (w < 1024) setVisible(2);
      else if (w < 1280) setVisible(3);
      else setVisible(4);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const maxIndex = Math.max(0, REVIEWS.length - visible);

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  const prev = useCallback(() => setIndex((i) => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);
  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);

  const cardWidthPct = 100 / visible;
  const translatePct = -(index * cardWidthPct);

  return (
    <section id="reviews" className="py-20 md:py-28 relative overflow-hidden" style={{ background: '#FAF8F5' }} data-testid="video-reviews-section">
      {/* Decorative ambient blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(201,169,110,0.18) 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(27,42,74,0.10) 0%, transparent 70%)' }} />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] mb-4" style={{ color: '#C9A96E' }}>
            <span className="w-8 h-px" style={{ background: '#C9A96E' }} />
            {t.kicker}
            <span className="w-8 h-px" style={{ background: '#C9A96E' }} />
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-balance leading-[1.05]" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.025em' }}>
            {t.title}
          </h2>
          <p className="mt-5 text-base md:text-[17px]" style={{ color: '#666' }}>{t.subtitle}</p>
        </div>

        {/* Carousel with side arrows */}
        <div className="relative">
          {/* Left arrow */}
          <button onClick={prev} aria-label="Previous"
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 -left-2 lg:-left-6 z-10 w-12 h-12 lg:w-14 lg:h-14 rounded-full items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-2xl"
            style={{ background: '#FFFFFF', border: '1px solid #1B2A4A', color: '#1B2A4A', boxShadow: '0 10px 28px -10px rgba(27,42,74,0.35)' }}
            data-testid="reviews-prev">
            <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
          {/* Right arrow */}
          <button onClick={next} aria-label="Next"
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-2 lg:-right-6 z-10 w-12 h-12 lg:w-14 lg:h-14 rounded-full items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-2xl"
            style={{ background: '#1B2A4A', color: '#C9A96E', boxShadow: '0 14px 32px -10px rgba(27,42,74,0.6)' }}
            data-testid="reviews-next">
            <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>

          <div className="relative overflow-hidden" data-testid="reviews-carousel">
            <div
              className="flex"
              style={{ transform: `translateX(${translatePct}%)`, transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            >
              {REVIEWS.map((r, i) => (
                <div key={r.id} className="flex-shrink-0 px-3" style={{ width: `${cardWidthPct}%` }}>
                  <VideoCard review={r} t={t} lang={lang} idx={i + 1} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dots + mobile arrows */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <button onClick={prev} aria-label="Previous"
            className="md:hidden w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#FFFFFF', border: '1px solid #1B2A4A', color: '#1B2A4A' }}
            data-testid="reviews-prev-mobile">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button key={i} onClick={() => setIndex(i)} aria-label={`Slide ${i + 1}`}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{ width: i === index ? 28 : 8, background: i === index ? '#C9A96E' : 'rgba(27,42,74,0.25)' }}
                data-testid={`reviews-dot-${i}`}
              />
            ))}
          </div>
          <button onClick={next} aria-label="Next"
            className="md:hidden w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#1B2A4A', color: '#C9A96E' }}
            data-testid="reviews-next-mobile">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default VideoReviews;

